import { BASE_BLUE, DARK_GREY, FONT } from 'src/lib/player/constants';
import tinycolor from 'tinycolor2';
import { type DrawInfo, DrawInfoType } from './createDrawInfos';

interface Measurement {
  textWidth: number;
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
}

const MARKER_HEIGHT = 22;
const MARKER_PADDING_LEFT = 5 + 11 + 7;
const MARKER_PADDING_RIGHT = 7;
export const POI_WIDTH = 40;
export const POI_HEIGHT = 40;
const POI_PADDING_LEFT = 10;
const POI_PADDING_RIGHT = 10;
const PARTICLE_MEASUREMENT = {
  textWidth: 0,
  x: 13,
  y: 13,
  offsetX: 6.5,
  offsetY: 6.5,
};
export const TRACK_HOVER_WIDTH = 40;

const measureCanvas = document.createElement('canvas');
const measureCanvasContext = measureCanvas.getContext('2d');
if (measureCanvasContext == null) {
  throw new Error("Couldn't create drawing canvas");
}

measureCanvasContext.font = FONT;

class MarkerContext {
  measureParticle() {
    return PARTICLE_MEASUREMENT;
  }

  measurePOI(label: string): Measurement {
    if (measureCanvasContext == null) throw new Error("Couldn't create drawing canvas");

    const textWidth = Math.ceil(measureCanvasContext.measureText(label).width);
    return {
      x: POI_WIDTH,
      y: POI_WIDTH,
      textWidth,
      offsetX: POI_WIDTH / 2,
      offsetY: POI_WIDTH,
    };
  }

  measurePOIImage(): Measurement {
    return {
      x: POI_WIDTH - 6,
      y: POI_HEIGHT - 1,
      offsetX: POI_WIDTH / 2,
      offsetY: POI_HEIGHT,
      textWidth: 0,
    };
  }

  measurePOILong(label: string): Measurement {
    if (measureCanvasContext == null) throw new Error("Couldn't create drawing canvas");

    const textWidth = Math.ceil(measureCanvasContext.measureText(label).width);
    return {
      x: Math.max(POI_WIDTH, textWidth + POI_PADDING_LEFT + POI_PADDING_RIGHT),
      y: POI_HEIGHT,
      textWidth,
      offsetX: POI_WIDTH / 2,
      offsetY: POI_HEIGHT,
    };
  }

  measureStarterMarker(label: string): Measurement {
    if (measureCanvasContext == null) throw new Error("Couldn't create drawing canvas");

    const textWidth = Math.ceil(measureCanvasContext.measureText(label).width);
    return {
      textWidth,
      x: textWidth + MARKER_PADDING_LEFT + MARKER_PADDING_RIGHT,
      y: MARKER_HEIGHT,
      offsetX: 10.5,
      offsetY: 11,
    };
  }

  measure(drawInfo: DrawInfo): Measurement {
    switch (drawInfo.type) {
      case DrawInfoType.POI:
        return this.measurePOI(drawInfo.label);
      case DrawInfoType.POI_LONG:
        return this.measurePOILong(drawInfo.label);
      case DrawInfoType.MARKER:
        return this.measureStarterMarker(drawInfo.label);
      case DrawInfoType.POI_IMAGE:
        return this.measurePOIImage();
      default:
        return this.measureParticle();
    }
  }

  drawParticle(ctx: CanvasRenderingContext2D, scale: number) {
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'white';
    ctx.strokeStyle = DARK_GREY;
    ctx.scale(scale, scale);

    ctx.beginPath();
    ctx.arc(6.5, 6.5, 4.5, Math.PI, 3 * Math.PI, false);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.scale(1 / scale, 1 / scale);
  }

  drawPOI(marker: DrawInfo, ctx: CanvasRenderingContext2D, scale: number) {
    /* eslint no-param-reassign: 0 */
    const {
      borderColor = '#ffffff',
      bgColor = '#36739a',
      textColor = '#ffffff',
      label = '',
    } = marker;

    const {
      textWidth,
      x: width,
      y: height,
    } = marker.type === DrawInfoType.POI_LONG ? this.measurePOILong(label) : this.measurePOI(label);

    ctx.globalAlpha = 1;
    ctx.fillStyle = tinycolor(bgColor).setAlpha(0.95).toRgbString();
    ctx.strokeStyle = borderColor;
    ctx.scale(scale, scale);

    ctx.beginPath();

    // ref: width=40, height=30
    ctx.moveTo(0.5, 2.5);
    // 0.5, 2.5
    ctx.arc(2.5, 2.5, 2, Math.PI, 1.5 * Math.PI, false);
    // 2.5, 0.5
    ctx.lineTo(width - 2.5, 0.5);
    // 37.5, 0.5
    ctx.arc(width - 2.5, 2.5, 2, 1.5 * Math.PI, 0, false);
    // 39.5, 2.5
    ctx.lineTo(width - 0.5, height - 6.5);
    // 39.5, 21.5
    ctx.arc(width - 2.5, height - 6.5, 2, 0, 0.5 * Math.PI, false);
    // 37, 27.5
    ctx.lineTo(2.5, height - 4.5);
    // 2.5, 24.5
    ctx.arc(2.5, height - 6.5, 2, 0.5 * Math.PI, Math.PI, false);
    // 0.5, 21.5
    ctx.lineTo(0.5, 2.5);
    // 0.5, 2.5

    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    if (!marker.tickless) {
      ctx.fillStyle = borderColor;
      ctx.beginPath();
      ctx.moveTo(width / 2 + 3.5, height - 4.5);
      // 23.5, 25.5
      ctx.lineTo(width / 2, height - 0.5);
      // 20, 29.5
      ctx.lineTo(width / 2 - 3.5, height - 4.5);
      // 16.5, 25.5

      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    ctx.fillStyle = textColor;

    // doing centering on our own, because auto-center causes weird ui bug
    const textX = Math.round((width - textWidth) / 2);
    ctx.fillText(label, textX, 23);
    ctx.scale(1 / scale, 1 / scale);
  }

  drawPOIWithImage(marker: DrawInfo, ctx: CanvasRenderingContext2D, scale: number) {
    if (marker.icon == null) return;
    if (marker.image == null) throw new Error('Image not prepared!');

    /* eslint no-param-reassign: 0 */
    const { borderColor = '#ffffff', bgColor = '#36739a' } = marker;

    const { x: width, y: height } = this.measurePOIImage();

    ctx.globalAlpha = 1;
    ctx.fillStyle = bgColor;
    ctx.strokeStyle = borderColor;
    ctx.scale(scale, scale);

    ctx.beginPath();

    // ref: width=40, height=30
    ctx.moveTo(0.5, 2.5);
    // 0.5, 2.5
    ctx.arc(2.5, 2.5, 2, Math.PI, 1.5 * Math.PI, false);
    // 2.5, 0.5
    ctx.lineTo(width - 2.5, 0.5);
    // 37.5, 0.5
    ctx.arc(width - 2.5, 2.5, 2, 1.5 * Math.PI, 0, false);
    // 39.5, 2.5
    ctx.lineTo(width - 0.5, height - 6.5);
    // 39.5, 21.5
    ctx.arc(width - 2.5, height - 6.5, 2, 0, 0.5 * Math.PI, false);
    // 37, 27.5
    ctx.lineTo(2.5, height - 4.5);
    // 2.5, 24.5
    ctx.arc(2.5, height - 6.5, 2, 0.5 * Math.PI, Math.PI, false);
    // 0.5, 21.5
    ctx.lineTo(0.5, 2.5);
    // 0.5, 2.5

    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    if (!marker.tickless) {
      ctx.fillStyle = borderColor;
      ctx.beginPath();
      ctx.moveTo(width / 2 + 3.5, height - 4.5);
      // 23.5, 25.5
      ctx.lineTo(width / 2, height - 0.5);
      // 20, 29.5
      ctx.lineTo(width / 2 - 3.5, height - 4.5);
      // 16.5, 25.5

      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    ctx.drawImage(marker.image, 1, 1, 32, 32);
    ctx.scale(1 / scale, 1 / scale);
  }

  drawStarterMarker(marker: DrawInfo, ctx: CanvasRenderingContext2D, scale: number) {
    /* eslint no-param-reassign: 0 */
    const {
      borderColor = '#ffffff',
      bgColor = BASE_BLUE,
      textColor = '#ffffff',
      label = '',
    } = marker;

    const { x: width, y: height } = this.measureStarterMarker(label);

    ctx.globalAlpha = 1;
    ctx.fillStyle = tinycolor(bgColor).setAlpha(0.95).toRgbString();
    ctx.strokeStyle = borderColor;
    ctx.scale(scale, scale);

    ctx.beginPath();
    ctx.moveTo(0.5, 5);
    // 0.5, 5
    ctx.arc(5, 5, 4.5, Math.PI, 1.5 * Math.PI, false);
    // 5, 0.5
    ctx.lineTo(width - 5, 0.5);
    // 18, 0.5
    ctx.arc(width - 5, 5, 4.5, 1.5 * Math.PI, 0, false);
    // 22.5, 5
    ctx.lineTo(width - 0.5, height - 4.5);
    // 22.5, 13
    ctx.arc(width - 5, height - 5, 4.5, 0, 0.5 * Math.PI, false);
    // 18, 17.5
    ctx.lineTo(5, height - 0.5);
    // 5, 17.5
    ctx.arc(5, height - 5, 4.5, 0.5 * Math.PI, Math.PI, false);
    // 0.5, 13
    ctx.lineTo(0.5, 5);
    // 0.5, 5

    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw clear circle
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(10.5, 11, 4.5, Math.PI, 3 * Math.PI, false);
    ctx.fill();
    ctx.restore();

    // Stroke circle
    ctx.beginPath();
    ctx.arc(10.5, 11, 4.5, Math.PI, 3 * Math.PI, false);
    ctx.closePath();
    ctx.stroke();

    // Stroke mini circle
    ctx.beginPath();
    ctx.arc(10.5, 11, 0.5, Math.PI, 3 * Math.PI, false);
    ctx.closePath();
    ctx.stroke();

    ctx.fillStyle = textColor;

    const textX = Math.round(MARKER_PADDING_LEFT);
    ctx.fillText(label, textX, 15);
    ctx.scale(1 / scale, 1 / scale);
  }

  draw(
    drawInfo: DrawInfo,
    ctx: CanvasRenderingContext2D,
    offsetX: number,
    offsetY: number,
    scale: number,
  ) {
    ctx.translate(offsetX, offsetY);
    switch (drawInfo.type) {
      case DrawInfoType.POI:
      case DrawInfoType.POI_LONG:
        this.drawPOI(drawInfo, ctx, scale);
        break;
      case DrawInfoType.POI_IMAGE:
        this.drawPOIWithImage(drawInfo, ctx, scale);
        break;
      case DrawInfoType.MARKER:
        this.drawStarterMarker(drawInfo, ctx, scale);
        break;
      default:
        this.drawParticle(ctx, scale);
        break;
    }
    ctx.translate(-offsetX, -offsetY);
  }
}

export default new MarkerContext();
